// src/views/FileManager/index.tsx
import React, { useMemo, useRef, useState } from 'react';
import { useNavigate, NavigateFunction, useLocation } from 'react-router-dom';
import { Typography } from 'antd';
import { VFSBrowser } from './components/VFSBrowser';
import './FileManager.scss';

import { useAuth } from '../../context/AuthContext';
import { AllowedRoles } from '../../routes/routeConstants/allowedRoles';
import type { FileManagerActions } from '../../types/FileManagerTypes';
import ConditionalRender from '../../shared/components/ConditionalRender';

const { Title } = Typography;

interface FileManagerProps {
  setFolderLocation?: (location: string) => void;
  setFolderId?: (location: string) => void;
}

const FileManagerContent: React.FC<FileManagerProps> = ({ setFolderId, setFolderLocation }) => {
  const navigate: NavigateFunction = useNavigate();
  const location = useLocation();
  const { canAccess } = useAuth();
  const canManageFiles = canAccess({ allowedRoles: AllowedRoles.EDITOR_AND_ABOVE });
  const fileManagerActionsRef = useRef<FileManagerActions>({
    startCreateFolder: () => {},
    openUpload: () => {},
  });
  // Mirrors VFSBrowser's inline-creation state so the footer can show the
  // active add-folder icon while a folder is being created.
  const [creatingFolder, setCreatingFolder] = useState(false);

  const getIsActiveClass = useMemo(() => (path: string) => (location.pathname === path ? 'active' : ''), [location.pathname]);

  const isTrashView = location.pathname === '/files/trash';
  const isRecentsView = location.pathname === '/files/recents';

  return (
    <>
      <header className="file-manager file-manager__header">
        <Title level={2}>FILE MANAGER {getTitleSuffix(location.pathname)}</Title>
      </header>
      <div className="file-manager  file-manager__content">
        <VFSBrowser
          setFolderLocation={setFolderLocation}
          setFolderId={setFolderId}
          fileManagerActionsRef={fileManagerActionsRef}
          onCreatingFolderChange={setCreatingFolder}
        />
      </div>
      <footer className="file-manager file-manager__footer">
        <hr />
        <div className="file-manager__footer-actions">
          <div className="file-manager__button-group">
            <img
              title="View Home"
              onClick={() => navigate('/files/all-files')}
              className={`file-manager__button ${getIsActiveClass('/files/all-files')}`}
              src={location.pathname === '/files/all-files' ? '/assets/home-selected.svg' : '/assets/files-home-icon.svg'}
              alt="View Home"
            />
            <img
              title="View Recent Files"
              onClick={() => navigate('/files/recents')}
              className={`file-manager__button ${getIsActiveClass('/files/recents')}`}
              src={isRecentsView ? '/assets/recents-active-icon.svg' : '/assets/recents-default-icon.svg'}
              alt="View Recent Files"
            />
            {canManageFiles && (
              <img
                title="Delete Files"
                onClick={() => navigate('/files/trash')}
                className={`file-manager__button ${getIsActiveClass('/files/trash')}`}
                src={isTrashView ? '/assets/trash-selected-icon.svg' : '/assets/trash-default-icon.svg'}
                alt="Delete Files"
              />
            )}
          </div>
          <ConditionalRender visible={canManageFiles && !isTrashView && !isRecentsView}>
            <div className="file-manager__button-group--right">
              <div
                className={`file-manager__button ${creatingFolder ? 'active' : ''}`}
                title="Add Folder"
                onClick={() => fileManagerActionsRef.current.startCreateFolder()}
              >
                <img src={creatingFolder ? '/assets/add-folder-icon-active.svg' : '/assets/add-folder-icon.svg'} alt="Add Folder" />
              </div>
              <div className="file-manager__button" title="Upload" onClick={() => fileManagerActionsRef.current.openUpload()}>
                <img src="/assets/upload-file-icon.svg" alt="Upload" />
              </div>
            </div>
          </ConditionalRender>
        </div>
      </footer>
    </>
  );
};

function getTitleSuffix(pathname: string): string {
  if (pathname === '/files/trash') return '| TRASH';
  if (pathname === '/files/all-files') return '| HOME';
  if (pathname === '/files/recents') return '| RECENT FILES';
  return '';
}

const FileManager: React.FC<FileManagerProps> = ({ setFolderId, setFolderLocation }) => (
  <div className="file-manager__layout">
    <FileManagerContent setFolderId={setFolderId} setFolderLocation={setFolderLocation} />
  </div>
);

export default FileManager;
